import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Layout, Row, Col, BackTop, message, Card } from 'antd';
import { withRouter } from 'react-router';
import {
	requestsForProposalRestCrudThunksForBuyer,
	requestsForProposalRestCrudThunksForSupplier,
} from '../../thunks/requests_for_proposal_thunks';
import { getRecordsForCompositeTargetCollection } from '../../reducers/standard_reducer_utils';
import { getLowestBidderSupplierFacilityId, nullSafeGet } from '../../utils/DataAccessUtils';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import {
	biddersRestCrudThunksForBuyer,
	biddersRestCrudThunksForSupplier,
	changeBidderAwardStatus,
} from '../../thunks/bidders_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import BidderCard from './BidderCard';
import BidderNotes from './BidderNotes/BidderNotes';
import { EmptyState } from '../empty_state/EmptyState';
import { FlexibleImageAvatar } from '../flexible-image-avatar/FlexibleImageAvatar';
import { bidderStatusTextFormatter } from '../../utils/DataFormatterUtils';
import BidderSendReminderButton from '../bidder_list/BidderSendReminderButton';
import ProposalOnBehalfOfSupplierButton from '../create_proposal_on_behalf_of_supplier_button/ProposalOnBehalfOfSupplierButton';

const RFP_DETAIL_BIDDERS = 'RFP_DETAIL_BIDDERS';

const { Content } = Layout;

const RFPDetailBidsNotesPage: FC<any> = ({
	getBidders,
	updateBidderAwardStatus,
	requestForProposal,
	bidders,
	requestsForProposalFetching,
	biddersFetching,
}): React.ReactElement => {
	const [awardUpdating, setAwardUpdating] = useState(false);
	const [lowestBidderSupplierFacilityId, setLowestBidderSupplierFacilityId] = useState(-1);
	const [selectedBidder, setSelectedBidder] = useState(null);

	useEffect(() => {
		const bidder = nullSafeGet(
			'0',
			getRecordsForCompositeTargetCollection(bidders, RFP_DETAIL_BIDDERS)
		);
		bidder && setSelectedBidder(bidder);
	}, []);

	const handleAwardStatusChange = useCallback(
		(requestForProposalId, supplierFacilityId, value) => {
			setAwardUpdating(true);
			updateBidderAwardStatus(requestForProposalId, supplierFacilityId, value)
				.then(() => {
					message.success(`Bidder status updated`);
					getBidders({ requestForProposalId }, RFP_DETAIL_BIDDERS);
				})
				.catch(message.error)
				.finally(() => setAwardUpdating(false));
		},
		[getBidders, updateBidderAwardStatus]
	);

	const associatedBidders = useMemo(
		() => getRecordsForCompositeTargetCollection(bidders, RFP_DETAIL_BIDDERS),
		[bidders]
	);
	const fetching = useMemo(
		() => requestsForProposalFetching || biddersFetching,
		[biddersFetching, requestsForProposalFetching]
	);

	useEffect(() => {
		setLowestBidderSupplierFacilityId(getLowestBidderSupplierFacilityId(associatedBidders));
	}, [associatedBidders]);

	const bidderAwarded = useMemo(
		() => (associatedBidders.filter((b) => b.awardStatus === 'awarded').length > 0 ? true : false),
		[associatedBidders]
	);

	if (fetching) {
		return <PageLoadingPlaceholder />;
	}

	return !associatedBidders.length ? (
		<EmptyState
			graphic={
				<img
					style={{ marginBottom: 8 }}
					src="https://s3.amazonaws.com/mock-data-assets/categories/images/letter owl.svg"
					alt="No bidders yet"
				/>
			}
			headline={'No bidders yet'}
		/>
	) : (
		<Content className="compareBidsPage" style={{ padding: '0 0.5em' }}>
			<BackTop />

			<Row style={{ margin: '0.5em 0' }} gutter={8}>
				<Col span={5}>
					<div
						style={{ borderRight: 'solid 1px rgb(229 231 235)' }}
						className="h-full  border-r border-owgray bg-white"
					>
						<div className="h-full px-2 py-4">
							<div className="block h-full w-full">
								<div className="mb-5 ml-2 text-2xl text-black">Notes</div>
								<div className="">
									{associatedBidders.map((bidder, idx) => (
										<li
											style={{ borderBottom: 'solid 1px #e5e7eb' }}
											key={nullSafeGet('supplierFacility.displayName', bidder)}
											className={`flex cursor-pointer border-b px-4 py-4 hover:bg-gray-100 hover:font-bold ${
												nullSafeGet('supplierFacilityId', selectedBidder) ==
												nullSafeGet('supplierFacilityId', bidder)
													? 'bg-gray-100 font-bold '
													: ''
											}`}
											onClick={() => {
												setSelectedBidder(bidder);
											}}
										>
											<div className="flex w-full flex-row items-center gap-4">
												<p className="mb-1  text-gray-900">
													{nullSafeGet('supplierFacility.displayName', bidder)}
												</p>
											</div>
										</li>
									))}
								</div>
							</div>
						</div>
					</div>
				</Col>
				<Col span={12}>
					<div className=" h-full w-full bg-white">
						<div className="bidder-chat-header flex flex-row justify-between gap-x-8 p-4">
							<div className="mb-5 flex flex-row items-center text-2xl text-black">
								<span style={{ marginRight: 16 }}>
									<FlexibleImageAvatar
										displayName={nullSafeGet('supplierFacility.displayName', selectedBidder)}
										showPopover={false}
										popoverText={nullSafeGet('supplierFacility.displayName', selectedBidder)}
										imageUrl={nullSafeGet('supplierFacility.logoURL', selectedBidder)}
										squareMode={true}
										width="40px"
										height="40px"
									/>
								</span>
								{nullSafeGet('supplierFacility.displayName', selectedBidder)}
							</div>
						</div>
						{nullSafeGet('supplierFacilityId', selectedBidder) ? (
							<BidderNotes selectedBidder={selectedBidder} />
						) : null}
					</div>
				</Col>
				<Col span={7}>
					<div className="flex w-full flex-row gap-x-8">
						{nullSafeGet('supplierFacilityId', selectedBidder) ? (
							nullSafeGet('proposalId', selectedBidder) ? (
								<BidderCard
									showSupplierInfo={false}
									size="large"
									key={`bidder-${nullSafeGet('supplierFacilityId', selectedBidder)}`}
									bidder={selectedBidder}
									bidderAwarded={bidderAwarded}
									awardUpdating={awardUpdating}
									requestForProposal={requestForProposal}
									handleAwardStatusChange={handleAwardStatusChange}
									lowestBidderSupplierFacilityId={lowestBidderSupplierFacilityId}
								/>
							) : (
								<Card className="w-full">
									<div className="flex w-full  flex-col items-center justify-center">
										<div className="mb-2">
											<span
												className={`bidder__statusText ${selectedBidder.bidderStatus}`}
												style={{ marginRight: 16 }}
											>
												{bidderStatusTextFormatter(selectedBidder.bidderStatus)}
											</span>
										</div>
										<span
											style={{ display: 'flex', position: 'relative', gap: 8, flexWrap: 'wrap' }}
										>
											<ProposalOnBehalfOfSupplierButton
												bidder={selectedBidder}
												requestForProposal={requestForProposal}
											/>
											<BidderSendReminderButton
												bidder={selectedBidder}
												requestForProposal={requestForProposal}
											/>
										</span>
									</div>
								</Card>
							)
						) : null}
					</div>
				</Col>
			</Row>
		</Content>
	);
};

const mapStateToProps = (state, ownProps) => ({
	requestForProposal: state.requests_for_proposal.detail,
	requestForProposalsFetching: state.requests_for_proposal.fetching,
	bidders: state.bidders,
	biddersFetching: state.bidders.fetching,
	proposals: state.proposals,
	match: ownProps.match,
	history: ownProps.history,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getRequestForProposal: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? requestsForProposalRestCrudThunksForSupplier.readOne(id)
				: requestsForProposalRestCrudThunksForBuyer.readOne(id)
		),
	updateBidderAwardStatus: (rfpId, supplierFacilityId, status) =>
		dispatch(changeBidderAwardStatus(ownProps.userType)(rfpId, supplierFacilityId, status)),
	getBidders: (params, targetCollectionName, pagination, sorting, filters) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? biddersRestCrudThunksForSupplier.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters
				  )
				: biddersRestCrudThunksForBuyer.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters
				  )
		),
});

const RFPDetailNotesPage = withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(RFPDetailBidsNotesPage)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(RFPDetailNotesPage);
